import { makeid } from 'utils/helpers/makeid';

import type { Prettify } from 'infrastructure/types';
import type { ExtractStepValues, TFormItem } from '../types';

export const initialTemplate_step_1 = [
  {
    key: makeid(10),
    title: <p>Confirm Initiating Visit</p>,
    description: '(E/M, AWV, TCM, or telehealth)',
    type: 'empty',
    name: '_empty',
    required: true,
    items: [
      {
        type: 'checkbox',
        name: 'confirmInitiatingVisit',
        title: null,
        key: makeid(10),
        options: [
          {
            label: 'Visit completed',
            value: 'visitCompleted',
            key: makeid(10),
            hideSubitemsIfParentNotSelected: true,
            subItems: [
              {
                key: makeid(10),
                type: 'input-date',
                name: 'visitDate',
                title: <i>Date:</i>,
                maxDate: new Date(),
              },
              {
                key: makeid(10),
                type: 'input',
                name: 'visitType',
                title: <i>Type:</i>,
              },
            ],
          },
          {
            label:
              'Established patient (visit within 3 years/eligible under CCM/PCM/ACO)',
            value: 'establishedPatient',
            key: makeid(10),
          },
        ],
      },
    ],
  },
] as const satisfies TFormItem[];

export const initialTemplate_step_2 = [
  {
    key: makeid(10),
    title: <p>Recent Healthcare Activity</p>,
    type: 'checkbox',
    required: true,
    name: 'recentHealthcareActivity',
    options: [
      {
        label: 'Provider appointment',
        value: 'providerAppointment',
        key: makeid(10),
      },
      {
        label: 'ER visit',
        value: 'erVisit',
        key: makeid(10),
      },
      {
        label: 'Inpatient stay',
        value: 'inpatientStay',
        key: makeid(10),
      },
      {
        label: 'Other',
        value: 'other',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'input',
            name: 'providerAppointmentOtherText',
          },
        ],
      },
    ],
    items: [
      {
        key: makeid(10),
        title: <p>Date(s)</p>,
        type: 'input',
        name: 'dates',
        required: true,
      },
      {
        key: makeid(10),
        title: <p>Details</p>,
        type: 'textarea',
        name: 'details',
        required: true,
      },
    ],
  },
] as const satisfies TFormItem[];

export const initialTemplate_step_3 = [
  {
    key: makeid(10),
    title: <p>Was MedRec performed?</p>,
    type: 'radio',
    required: true,
    name: 'wasMedRecPerformed',
    options: [
      {
        label: 'Yes',
        value: 'yes',
        key: makeid(10),
      },
      {
        label: 'No',
        value: 'no',
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Timing</p>,
    type: 'checkbox',
    required: true,
    name: 'timing',
    options: [
      {
        key: makeid(10),
        label: 'During routine assessment',
        value: 'duringRoutineAssessment',
      },
      {
        key: makeid(10),
        label: 'In advance of appointment on:',
        value: 'inAdvanceOfAppointmentOn',
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'input',
            required: true,
            name: 'inAdvanceOfAppointmentOnText',
          },
        ],
      },
      {
        key: makeid(10),
        label: 'After ER',
        value: 'afterER',
      },
      {
        key: makeid(10),
        label: 'After hospitalization',
        value: 'afterHospitalization',
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Medication discrepancies?</p>,
    type: 'radio',
    required: true,
    name: 'medicationDiscrepancies',
    options: [
      {
        label: 'Yes',
        value: 'yes',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'textarea',
            required: true,
            placeholder: 'Describe',
            name: 'medicationDiscrepanciesYesText',
          },
        ],
      },
      {
        label: 'None',
        value: 'none',
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Patient taking medications as prescribed?</p>,
    type: 'radio',
    required: true,
    name: 'patientTakingMedicationsAsPrescribed',
    options: [
      {
        label: 'Yes',
        value: 'yes',
        key: makeid(10),
      },
      {
        label: 'No',
        value: 'no',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'textarea',
            required: true,
            placeholder: 'Give reason',
            name: 'patientTakingMedicationsAsPrescribedNoText',
          },
        ],
      },
    ],
  },
] as const satisfies TFormItem[];

export const initialTemplate_step_4 = [
  {
    key: makeid(10),
    title: <p>Chronic Conditions Managed</p>,
    description: '(minimum of 2 required)',
    type: 'empty',
    name: '_empty',
    required: true,
    items: [
      {
        key: makeid(10),
        title: <i>Condition 1:</i>,
        type: 'checkbox',
        name: 'condition1',
        options: [
          {
            label: 'Diagnosis:',
            value: 'condition1Diagnosis',
            key: makeid(10),
            hideSubitemsIfParentNotSelected: true,
            subItems: [
              {
                key: makeid(10),
                title: null,
                type: 'input',
                name: 'condition1DiagnosisText',
              },
              {
                key: makeid(10),
                title: <i>ICD:</i>,
                type: 'input',
                name: 'condition1DiagnosisICDText',
              },
            ],
          },
          {
            label: 'Current interventions:',
            value: 'condition1CurrentInterventions',
            key: makeid(10),
            hideSubitemsIfParentNotSelected: true,
            subItems: [
              {
                key: makeid(10),
                title: null,
                type: 'input',
                name: 'condition1CurrentInterventionsText',
              },
            ],
          },
          {
            label: 'Measurable goal(s):',
            value: 'condition1MeasurableGoal',
            key: makeid(10),
            hideSubitemsIfParentNotSelected: true,
            subItems: [
              {
                key: makeid(10),
                title: null,
                type: 'input',
                name: 'condition1MeasurableGoalText',
              },
            ],
          },
        ],
      },
      {
        key: makeid(10),
        title: <i>Condition 2:</i>,
        type: 'checkbox',
        name: 'condition2',
        options: [
          {
            label: 'Diagnosis:',
            value: 'condition2Diagnosis',
            key: makeid(10),
            hideSubitemsIfParentNotSelected: true,
            subItems: [
              {
                key: makeid(10),
                title: null,
                type: 'input',
                name: 'condition2DiagnosisText',
              },
              {
                key: makeid(10),
                title: <i>ICD:</i>,
                type: 'input',
                name: 'condition2DiagnosisICDText',
              },
            ],
          },
          {
            label: 'Current interventions:',
            value: 'condition2CurrentInterventions',
            key: makeid(10),
            hideSubitemsIfParentNotSelected: true,
            subItems: [
              {
                key: makeid(10),
                title: null,
                type: 'input',
                required: true,
                name: 'condition2CurrentInterventionsText',
              },
            ],
          },
          {
            label: 'Measurable goal(s):',
            value: 'condition2MeasurableGoal',
            key: makeid(10),
            hideSubitemsIfParentNotSelected: true,
            subItems: [
              {
                key: makeid(10),
                title: null,
                type: 'input',
                required: true,
                name: 'condition2MeasurableGoalText',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    key: makeid(10),
    html: <p>Care Plan Creation</p>,
    type: 'info',
    name: '_info',
  },
  {
    key: makeid(10),
    title: <p>Individualized care plan created?</p>,
    type: 'radio',
    required: true,
    name: 'individualizedCarePlanCreated',
    options: [
      {
        label: 'Yes',
        value: 'yes',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'checkbox',
            required: true,
            name: 'carePlanCreatedYesList',
            options: [
              {
                key: makeid(10),
                label: 'Interventions',
                value: 'interventions',
                hideSubitemsIfParentNotSelected: true,
                subItems: [
                  {
                    key: makeid(10),
                    title: null,
                    type: 'checkbox',
                    required: true,
                    name: 'interventionsList',
                    options: [
                      {
                        key: makeid(10),
                        label: 'Medication Management',
                        value: 'medicationManagement',
                        hideSubitemsIfParentNotSelected: true,
                        subItems: [
                          {
                            key: makeid(10),
                            title: null,
                            type: 'checkbox',
                            required: true,
                            name: 'medicationManagementList',
                            options: [
                              {
                                key: makeid(10),
                                label:
                                  'Review and reconcile patient’s medications at each encounter',
                                value: 'reviewAndReconcilePatientMedications',
                              },
                              {
                                key: makeid(10),
                                label:
                                  'Ensure adherence to prescribed regimen for HTN, CHF, and COPD',
                                value: 'ensureAdherenceToPrescribedRegimen',
                              },
                            ],
                          },
                        ],
                      },
                      {
                        key: makeid(10),
                        label: 'Education & Self-Monitoring',
                        value: 'educationAndSelfMonitoring',
                        hideSubitemsIfParentNotSelected: true,
                        subItems: [
                          {
                            key: makeid(10),
                            title: null,
                            type: 'checkbox',
                            required: true,
                            name: 'educationAndSelfMonitoringList',
                            options: [
                              {
                                key: makeid(10),
                                label:
                                  'Educate patient about recognizing warning signs: elevated BP, shortness of breath, swelling, and increased cough',
                                value:
                                  'educatePatientAboutRecognizingWarningSigns',
                              },
                              {
                                key: makeid(10),
                                label:
                                  'Encourage daily weight monitoring (CHF), blood pressure checks (HTN), and symptom tracking (COPD)',
                                value: 'encourageDailyWeightMonitoring',
                              },
                              {
                                key: makeid(10),
                                label:
                                  'Provide guidance on inhaler technique and fluid/salt restriction as appropriate',
                                value: 'provideGuidanceOnInhalerTechnique',
                              },
                            ],
                          },
                        ],
                      },
                      {
                        key: makeid(10),
                        label: 'Care Coordination',
                        value: 'careCoordination',
                        hideSubitemsIfParentNotSelected: true,
                        subItems: [
                          {
                            key: makeid(10),
                            title: null,
                            type: 'checkbox',
                            required: true,
                            name: 'careCoordinationList',
                            options: [
                              {
                                key: makeid(10),
                                label:
                                  'Schedule and facilitate follow-up appointments with PCP and specialists',
                                value:
                                  'scheduleAndFacilitateFollowUpAppointments',
                              },
                              {
                                key: makeid(10),
                                label:
                                  'Provide resources for smoking cessation if patient is a tobacco user',
                                value: 'provideResourcesForSmokingCessation',
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                key: makeid(10),
                label: 'Goals',
                value: 'goals',
                hideSubitemsIfParentNotSelected: true,
                subItems: [
                  {
                    key: makeid(10),
                    title: null,
                    type: 'checkbox',
                    required: true,
                    name: 'goalList',
                    options: [
                      {
                        key: makeid(10),
                        label:
                          'Manage blood pressure, fluid retention, and breathing symptoms to reduce risk of exacerbation and hospitalizations. (CHF. CKD. and COPD DX)',
                        value:
                          'manageBloodPressureFluidRetentionAndBreathingSymptoms',
                      },
                      {
                        key: makeid(10),
                        label:
                          'Manage blood pressure to reduce risk of exacerbation and hospitalizations. (HTN DX)',
                        value: 'manageBloodPressureToReduceRisk',
                      },
                      {
                        key: makeid(10),
                        label:
                          'Improve daily functioning and quality of life. (All Conditions)',
                        value: 'improveDailyFunctioningAndQualityOfLife',
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        label: 'No',
        value: 'no',
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Barriers:</p>,
    description: '(select all that apply)',
    type: 'checkbox',
    required: true,
    name: 'barriers',
    options: [
      {
        key: makeid(10),
        label: 'Medication cost',
        value: 'medicationCost',
      },
      {
        label: 'Transportation',
        value: 'transportation',
        key: makeid(10),
      },
      {
        label: 'Forgetfulness',
        value: 'forgetfulness',
        key: makeid(10),
      },
      {
        label: 'Health literacy',
        value: 'healthLiteracy',
        key: makeid(10),
      },
      {
        label: 'Social isolation',
        value: 'socialIsolation',
        key: makeid(10),
      },
      {
        label: 'Pain/disability',
        value: 'painDisability',
        key: makeid(10),
      },
      {
        label: 'Financial strain',
        value: 'financialStrain',
        key: makeid(10),
      },
      {
        label: 'No support at home',
        value: 'noSupportAtHome',
        key: makeid(10),
      },
      {
        label: 'Behavioral health/depression',
        value: 'behavioralHealthDepression',
        key: makeid(10),
      },
      {
        label: 'Nonadherence (meds/appts)',
        value: 'nonadherence',
        key: makeid(10),
      },
      {
        label: 'Other',
        value: 'other',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            type: 'textarea',
            name: 'barriersOtherText',
            title: null,
            required: true,
          },
        ],
      },
    ],
  },
  {
    key: makeid(10),
    type: 'textarea',
    title: <p>Additional barrier notes</p>,
    name: 'additionalBarrierNotes',
    required: true,
  },
  {
    key: makeid(10),
    type: 'checkbox',
    title: <p>Plan includes:</p>,
    name: 'planIncludes',
    required: true,
    options: [
      {
        label: 'Interventions for each condition',
        value: 'interventionsForEachCondition',
        key: makeid(10),
      },
      {
        label: 'Measurable patient goals per condition',
        value: 'measurablePatientGoalsPerCondition',
        key: makeid(10),
      },
    ],
  },
] as const satisfies TFormItem[];

export const initialTemplate_step_5 = [
  {
    key: makeid(10),
    title: <p>PHQ-2 completed?</p>,
    type: 'radio',
    required: true,
    name: 'phq2Completed',
    options: [
      {
        label: 'Yes',
        value: 'yes',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'input',
            required: true,
            placeholder: 'Score',
            name: 'phq2CompletedYesText',
          },
        ],
      },
      {
        label: 'No',
        value: 'no',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'textarea',
            required: true,
            placeholder: 'Reason',
            name: 'phq2CompletedNoText',
          },
        ],
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>SDOH Assessment completed?</p>,
    type: 'radio',
    required: true,
    name: 'sdohAssessmentCompleted',
    options: [
      {
        label: 'Yes',
        value: 'yes',
        key: makeid(10),
      },
      {
        label: 'No',
        value: 'no',
        key: makeid(10),
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Any unmet needs?</p>,
    type: 'radio',
    required: true,
    name: 'anyUnmetNeeds',
    options: [
      {
        label: 'Yes',
        value: 'yes',
        key: makeid(10),
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'textarea',
            required: true,
            placeholder: 'Describe',
            name: 'anyUnmetNeedsYesText',
          },
        ],
      },
      {
        label: 'No',
        value: 'no',
        key: makeid(10),
      },
    ],
  },
] as const satisfies TFormItem[];

export type Step_1_Values = ExtractStepValues<typeof initialTemplate_step_1>;
export type Step_2_Values = ExtractStepValues<typeof initialTemplate_step_2>;
export type Step_3_Values = ExtractStepValues<typeof initialTemplate_step_3>;
export type Step_4_Values = ExtractStepValues<typeof initialTemplate_step_4>;
export type Step_5_Values = ExtractStepValues<typeof initialTemplate_step_5>;

export type InitialSteppedValues = {
  STEP1: Prettify<Partial<Step_1_Values>>;
  STEP2: Prettify<Partial<Step_2_Values>>;
  STEP3: Prettify<Partial<Step_3_Values>>;
  STEP4: Prettify<Partial<Step_4_Values>>;
  STEP5: Prettify<Partial<Step_5_Values>>;
};

type StepType = { title: string; template: TFormItem[] };

export const steppedTemplates = {
  0: {
    title: 'Initial Assessment & Care Plan Build',
    template: initialTemplate_step_1,
  },
  1: {
    title: 'Initial Patient Assessment',
    template: initialTemplate_step_2,
  },
  2: {
    title: 'Medication Reconciliation',
    template: initialTemplate_step_3,
  },
  3: {
    title: 'Chronic Conditions Managed',
    template: initialTemplate_step_4,
  },
  4: {
    title: 'Patient Self-Reported Assessment',
    template: initialTemplate_step_5,
  },
} as const satisfies Record<number, StepType>;

export type TInitialTemplateValues = Partial<
  Step_1_Values & Step_2_Values & Step_3_Values & Step_4_Values & Step_5_Values
>;
